import { returnToSelect, type SelectReturnPage } from "../change-character.ts";

/**
 * The queue window's Leave queue as the page runs it (P1.4-T164; ADR-127 decision 5 and question 8, the author's
 * answer of 2026-10-11: the line starts after character select, so leaving it returns there). The flow is the page's
 * order made explicit, so it is unit-tested without a page; `main.ts` gives it the page's parts.
 */

/**
 * What the flow needs from the page: the way back to character select, and the message to the gateway.
 */
export interface LeaveQueuePage extends SelectReturnPage {
  /**
   * Sends `queueLeave` on the page's connection, which ends the place the gateway holds for the account
   * (`GameConnection.sendQueueLeave`).
   *
   * @returns Whether it was sent: false once the gateway has welcomed the page, or with no open connection.
   */
  readonly leave: () => boolean;
}

/**
 * Leaves the login queue: tells the gateway first, since a connection that only closes keeps its place for 2
 * minutes (ADR-127 decision 4), then returns to character select as Change character does once confirmed, with no
 * question, since leaving costs the player nothing they hold. A page the gateway has already welcomed stays where it
 * is, joining the world (the click crossed its `welcome`). A `queueLeave` that was sent and crosses the `welcome` on
 * the wire is answered by the gateway as a logout, a clean close with no error (PROTOCOL 6.64.0), by which time the
 * page is on its way to character select. A `?token=` page has no select: it tells the gateway, whose close then
 * shows the shell's notice with Retry.
 *
 * @param page What the flow needs from the page.
 * @returns True once the gateway was told, false when the page stayed where it was.
 */
export async function leaveQueue(page: LeaveQueuePage): Promise<boolean> {
  if (!page.leave()) {
    return false;
  }
  if (page.current() !== null) {
    await returnToSelect(page);
  }
  return true;
}
